import { useEffect, useState, useCallback, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Check, RotateCcw, UploadCloud, Loader2, Type, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { supabase } from "@/lib/supabaseClient";
import { applyTypography } from "@/lib/appearance";
import { cn } from "@/lib/utils";

const BASE = process.env.REACT_APP_BACKEND_URL;

export function TypographyPanel({ isAdmin }) {
  const [presets, setPresets] = useState([]);
  const [fonts, setFonts] = useState([]);
  const [current, setCurrent] = useState(null); // resolved typography {roles,controls,preset_id}
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [meta, setMeta] = useState({ family: "", weight: "400", style: "normal", role_hint: "body", supports_devanagari: false, license_note: "" });

  const load = useCallback(async () => {
    try { setPresets(await apiFetch("/typography-presets")); } catch (e) { toast.error(e.message); }
    try { setFonts(await apiFetch("/fonts")); } catch { /* non-fatal */ }
    try {
      const a = await apiFetch("/appearance");
      setCurrent(a.typography || null);
      if (a.typography) applyTypography(a.typography, a.custom_fonts || []);
    } catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { load().catch((e) => toast.error(e.message)); }, [load]);

  const selectPreset = async (id) => {
    if (!isAdmin) return toast.error("Only administrators can change organisation typography.");
    try {
      const res = await apiFetch("/appearance/typography", { method: "PUT", body: JSON.stringify({ typography_preset_id: id }) });
      setCurrent(res.typography); applyTypography(res.typography, fonts);
      toast.success("Typography updated for the organisation");
    } catch (e) { toast.error(e.message); }
  };
  const reset = async () => {
    try {
      const res = await apiFetch("/appearance/typography", { method: "PUT", body: JSON.stringify({ reset: true }) });
      setCurrent(res.typography); applyTypography(res.typography, fonts);
      toast.success("Reset to Nirvana Default typography");
    } catch (e) { toast.error(e.message); }
  };
  const uploadFont = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!meta.family.trim()) return toast.error("Please enter the font family name first.");
    setBusy(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const form = new FormData();
      form.append("file", file);
      Object.entries(meta).forEach(([k, v]) => form.append(k, String(v)));
      const res = await fetch(`${BASE}/api/fonts`, { method: "POST", body: form, headers: { Authorization: `Bearer ${session?.access_token}` } });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Upload failed");
      toast.success(`Added “${data.family}”`);
      setMeta({ family: "", weight: "400", style: "normal", role_hint: "body", supports_devanagari: false, license_note: "" });
      await load();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  const archiveFont = async (id) => {
    try { await apiFetch(`/fonts/${id}/archive`, { method: "PUT" }); toast.success("Font archived"); await load(); }
    catch (e) { toast.error(e.message); }
  };

  const roles = current?.roles || {};

  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="typography-panel">
      {/* presets + font library */}
      <div className="space-y-6">
        <Card className="p-5 shadow-soft">
          <h3 className="mb-1 font-heading text-lg font-semibold">Typography presets</h3>
          <p className="mb-4 text-xs text-muted-foreground">Reusable font sets applied across ContentOS. {isAdmin ? "" : "Managed by administrators."}</p>
          <div className="space-y-2">
            {presets.map((p) => {
              const active = current?.preset_id === p.id;
              return (
                <button key={p.id} disabled={!isAdmin} onClick={() => selectPreset(p.id)} data-testid={`typo-preset-${p.id}`}
                  aria-pressed={active} data-selected={active}
                  className={cn("flex w-full items-center justify-between rounded-lg border p-3 text-left transition-colors",
                    active ? "border-accent bg-accent-subtle" : "border-border hover:bg-muted/50", !isAdmin && "cursor-not-allowed opacity-70")}>
                  <div>
                    <div className="text-sm font-medium">{p.name} {p.is_preset && <span className="ml-1 text-[10px] uppercase text-muted-foreground">Built-in</span>}</div>
                    <div className="text-xs text-muted-foreground">{(p.config?.roles?.heading) || "—"} · {(p.config?.roles?.body) || "—"}</div>
                  </div>
                  {active && <Check className="h-4 w-4 text-accent" />}
                </button>
              );
            })}
          </div>
          {isAdmin && (
            <div className="mt-4 flex justify-end">
              <Button variant="outline" size="sm" className="gap-1.5" onClick={reset} data-testid="typo-reset"><RotateCcw className="h-4 w-4" /> Reset to Nirvana Default</Button>
            </div>
          )}
        </Card>

        {isAdmin && (
          <Card className="p-5 shadow-soft" data-testid="font-library">
            <div className="mb-1 flex items-center gap-2"><Type className="h-4 w-4 text-primary" /><h3 className="font-heading text-lg font-semibold">Font library</h3></div>
            <p className="mb-3 text-xs text-muted-foreground">Upload approved WOFF2/WOFF/TTF/OTF fonts. SVG fonts, scripts and CSS are blocked.</p>
            <div className="mb-3 flex items-start gap-2 rounded-lg border border-accent/40 bg-accent-subtle/60 px-3 py-2 text-xs text-muted-foreground">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
              Your organisation must hold a licence to use and embed any font you upload.
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input placeholder="Font family name" value={meta.family} onChange={(e) => setMeta({ ...meta, family: e.target.value })} data-testid="font-family" />
              <Select value={meta.role_hint} onValueChange={(v) => setMeta({ ...meta, role_hint: v })}>
                <SelectTrigger data-testid="font-role"><SelectValue /></SelectTrigger>
                <SelectContent>{["brand", "heading", "body", "ui", "devanagari"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="Weight (e.g. 400)" value={meta.weight} onChange={(e) => setMeta({ ...meta, weight: e.target.value })} />
              <Input placeholder="Licence / source note" value={meta.license_note} onChange={(e) => setMeta({ ...meta, license_note: e.target.value })} />
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm"><Switch checked={meta.supports_devanagari} onCheckedChange={(v) => setMeta({ ...meta, supports_devanagari: v })} /> Supports Devanagari</label>
            <input ref={fileRef} type="file" accept=".woff2,.woff,.ttf,.otf" className="hidden" onChange={uploadFont} data-testid="font-file" />
            <Button className="mt-3 gap-1.5" size="sm" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="font-upload">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Upload font
            </Button>
            {fonts.length > 0 && (
              <div className="mt-4 space-y-2">
                {fonts.map((f) => (
                  <div key={f.id} className="flex items-center justify-between rounded-lg border border-border p-2.5 text-sm" data-testid={`font-${f.id}`}>
                    <span style={{ fontFamily: `"${f.family}"` }}>{f.family} <span className="text-xs text-muted-foreground">· {f.format} · {f.role_hint}</span></span>
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => archiveFont(f.id)}>Archive</Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}
      </div>

      {/* preview studio */}
      <Card className="p-6 shadow-soft" data-testid="typography-preview">
        <div className="mb-4 text-sm font-medium">Preview studio</div>
        <div className="space-y-4">
          <div className="rounded-xl border border-border p-4">
            <div className="font-brand text-3xl font-bold tracking-tight">NIRVANA</div>
            <div className="text-sm font-medium text-accent">{current?.controls ? "ContentOS" : "ContentOS"}</div>
          </div>
          <div>
            <h2 className="font-heading text-2xl font-semibold">Owner Control Centre</h2>
            <p className="font-body-reading text-sm text-muted-foreground">Here is what needs your attention today. Longer reading text uses the body font for comfortable reading across editorial surfaces.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm">Primary button</Button>
            <Button size="sm" variant="outline">Secondary</Button>
            <span className="rounded-md bg-secondary px-2 py-1 text-xs font-medium">UI label</span>
          </div>
          <div className="rounded-lg border border-border p-3">
            <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Task card</div>
            <div className="text-sm font-medium">Review festival blog draft</div>
            <div className="font-mono text-xs text-muted-foreground">ID: TASK-0001</div>
          </div>
          <div className="rounded-lg border border-border p-3">
            <div className="mb-1 text-xs text-muted-foreground">Devanagari</div>
            <div className="font-deva text-2xl">ॐ नमः शिवाय</div>
          </div>
          <div className="text-xs text-muted-foreground">
            Fonts: brand <b>{roles.brand}</b> · heading <b>{roles.heading}</b> · body <b>{roles.body}</b> · UI <b>{roles.ui}</b> · mono <b>{roles.mono}</b> · Devanagari <b>{roles.devanagari}</b>
          </div>
        </div>
      </Card>
    </div>
  );
}
